import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  Search,
  RefreshCw,
  AlertCircle,
  Download,
  Eye,
  User,
  Calendar,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../../components/ui';
import { formatDate } from '../../lib/utils';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface AuditLog {
  id: string;
  action: string;
  resource: string;
  resourceId?: string;
  userId?: string;
  user?: {
    name: string;
    email: string;
  };
  ipAddress?: string;
  userAgent?: string;
  oldData?: Record<string, unknown>;
  newData?: Record<string, unknown>;
  createdAt: string;
}

const actionColors: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
  CREATE: 'success',
  UPDATE: 'warning',
  DELETE: 'error',
  LOGIN: 'info',
  LOGOUT: 'info',
};

export function AuditLogs() {
  const [searchTerm, setSearchTerm] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['audit-logs', resourceFilter],
    queryFn: async () => {
      const params: Record<string, string> = { limit: '100' };
      if (resourceFilter) params.resource = resourceFilter;
      const response = await api.get(ENDPOINTS.audit.logs, { params });
      return response.data as { data: AuditLog[]; total: number };
    },
  });

  const logs = data?.data || [];
  const filteredLogs = logs.filter(log =>
    log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.resource.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.user?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const uniqueResources = [...new Set(logs.map(log => log.resource))];

  const handleExport = async () => {
    try {
      const response = await api.get(ENDPOINTS.audit.export, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Erro ao exportar logs:', err);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar logs</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Logs de Auditoria</h1>
          <p className="text-surface-400">Histórico de ações realizadas no sistema</p>
        </div>
        <Button variant="secondary" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Exportar
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Buscar por ação, recurso ou usuário..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select
              value={resourceFilter}
              onChange={(e) => setResourceFilter(e.target.value)}
              options={[
                { value: '', label: 'Todos os recursos' },
                ...uniqueResources.map(r => ({ value: r, label: r })),
              ]}
              className="w-48"
            />
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Logs List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary-400" />
            Registros de Auditoria
            <Badge variant="info">{filteredLogs.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-16" />
              ))}
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum log encontrado</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {filteredLogs.map(log => (
                <div
                  key={log.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <Badge variant={actionColors[log.action] || 'info'}>
                      {log.action}
                    </Badge>
                    <div>
                      <p className="font-medium text-surface-100">
                        {log.resource}
                        {log.resourceId && <span className="text-surface-500 ml-2">#{log.resourceId.slice(0, 8)}</span>}
                      </p>
                      <div className="flex items-center gap-3 text-sm text-surface-500">
                        {log.user && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {log.user.name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(log.createdAt)}
                        </span>
                        {log.ipAddress && (
                          <span>IP: {log.ipAddress}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedLog(log)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title="Detalhes do Log"
      >
        {selectedLog && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-surface-500">Ação</p>
                <Badge variant={actionColors[selectedLog.action] || 'info'}>
                  {selectedLog.action}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-surface-500">Recurso</p>
                <p className="text-surface-100">{selectedLog.resource}</p>
              </div>
              <div>
                <p className="text-sm text-surface-500">ID do Recurso</p>
                <p className="text-surface-100 font-mono text-sm">{selectedLog.resourceId || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-surface-500">Data/Hora</p>
                <p className="text-surface-100">{formatDate(selectedLog.createdAt)}</p>
              </div>
              <div>
                <p className="text-sm text-surface-500">Usuário</p>
                <p className="text-surface-100">{selectedLog.user?.name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-surface-500">IP</p>
                <p className="text-surface-100 font-mono text-sm">{selectedLog.ipAddress || '-'}</p>
              </div>
            </div>

            {selectedLog.oldData && (
              <div>
                <p className="text-sm text-surface-500 mb-2">Dados Anteriores</p>
                <pre className="bg-surface-900 p-3 rounded-lg text-xs overflow-auto max-h-40">
                  {JSON.stringify(selectedLog.oldData, null, 2)}
                </pre>
              </div>
            )}

            {selectedLog.newData && (
              <div>
                <p className="text-sm text-surface-500 mb-2">Dados Novos</p>
                <pre className="bg-surface-900 p-3 rounded-lg text-xs overflow-auto max-h-40">
                  {JSON.stringify(selectedLog.newData, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </motion.div>
  );
}
